/**
 * Preferences React Context.
 *
 * Owns the per-user **working state** — the live, applied UI
 * configuration (preferences schema v3 ``working_state``, a full Profile
 * document; ADR-033 §3.2). The rest of the app reads slices of it via
 * derived helpers (theme, layout, entity-detail defaults, the dynamic-
 * track map setting); applying a Profile or tweaking a setting re-renders
 * those consumers live.
 *
 * **Settings are always live (architect 2026-06-08, part 1).** The Settings
 * panel edits the working state in place through :func:`setWorkingState`;
 * every change persists immediately. There is no draft / preview overlay —
 * the only "undo" is re-applying a Profile (Apply / Reset-to-profile).
 *
 * The mutators are **optimistic**: they update the in-memory state
 * immediately, then issue ``PUT /api/me/preferences`` in the background
 * and roll back on failure. The gear-menu surfaces that rollback to the
 * user via an inline error banner per Flow P edge cases.
 *
 * Cross-references:
 *   - REQ-AUTH-053..058 (preferences storage + endpoints).
 *   - REQ-AUTH-070 (schema v3 — working_state + active_profile).
 *   - REQ-UI-040 (theme value); REQ-UI-044 (cache applied before paint).
 *   - REQ-UI-073 / 075 (working-state model; Apply copies by value).
 *   - REQ-UI-074 (active-Profile + "(modified)" marker).
 *   - ADR-022 §Feature 3, ADR-033 §3.2 / §4.2.
 */
import { createContext, useContext } from "react";
import type {
  ActiveProfileRef,
  DefaultProfileRef,
  ProfileDocument,
  UserPreferences,
} from "@/auth/session-types";

/**
 * The result of a preferences mutation. ``status === "ok"`` means the BFF
 * accepted the PUT; ``status === "error"`` means the PUT failed *and* the
 * in-memory state has been rolled back to the pre-call value. The menu
 * surfaces the error message inline; the gear icon's tooltip never lies.
 */
export type SetPreferencesResult =
  { status: "ok" } | { status: "error"; message: string; requestId?: string };

export interface PreferencesContextValue {
  /** Current preferences document (schema v3) — server source of truth,
   *  plus optimistic-mutation overlay during in-flight PUTs. Most
   *  consumers should read :attr:`workingState` instead of reaching into
   *  this directly. */
  preferences: UserPreferences;
  /** The live working state — the applied Profile document the rest of
   *  the app renders (ADR-033 §3.2). Never ``undefined``: the provider
   *  fills the default document when the BFF returns an empty preferences
   *  payload. */
  workingState: ProfileDocument;
  /** The most-recently-applied Profile pointer, or ``null`` when the
   *  working state is unnamed / custom (fresh migration, or after the
   *  active Profile was deleted). Drives the gear menu's
   *  "Active: «Name»" line + "(modified)" marker (REQ-UI-074). */
  activeProfile: ActiveProfileRef | null;
  /** The user's selected login-default Profile pointer, or ``null`` when
   *  none is set (the UI falls back to the built-in Shared default at
   *  login). Drives the gear menu's "Set as default" / current-default
   *  marker (ADR-033 §3.2; REQ-UI-074). */
  defaultProfile: DefaultProfileRef | null;
  /**
   * Merge a partial document into the working state and persist it. The
   * patch is shallow-merged at the top level of the Profile document
   * (e.g. ``{ theme }``, ``{ layout }``, ``{ map }``) — callers that need
   * to update a nested slice read the current sub-object, spread their
   * change in, and pass the whole sub-object. Optimistic + rolls back on
   * PUT failure. The full working state (units / clocks / map /
   * entity_details / layout) is round-tripped so a single-slice edit never
   * drops another increment's data (REQ-AUTH-060 round-trip discipline).
   */
  setWorkingState: (patch: Partial<ProfileDocument>) => Promise<SetPreferencesResult>;
  /**
   * Apply a Profile document to the working state (REQ-UI-075): replace
   * the working state with ``document`` (copy-by-value — later tweaks
   * never mutate the saved Profile), set ``active_profile`` to ``ref``,
   * and ``PUT``. The caller (the Profiles layer) is responsible for
   * computing ``ref.applied_document_sha256`` over the document.
   */
  applyWorkingState: (
    document: ProfileDocument,
    ref: ActiveProfileRef,
  ) => Promise<SetPreferencesResult>;
  /**
   * Replace the entire preferences document. Lower-level escape hatch
   * retained for the theme quick-toggle and tests; prefer
   * :func:`setWorkingState` for working-state edits. Optimistic; rolls
   * back on failure.
   */
  setPreferences: (next: UserPreferences) => Promise<SetPreferencesResult>;
  /**
   * Reset preferences to default — resets the working state to the
   * default document, clears ``active_profile``, and clears the
   * localStorage cache. Implemented as a PUT with defaults rather than
   * DELETE because the BFF surface only exposes GET/PUT today.
   */
  resetPreferences: () => Promise<SetPreferencesResult>;
  /**
   * Set (or clear, with ``null``) the user's login-default Profile pointer
   * and persist it via ``PUT /api/me/preferences`` (ADR-033 §3.2; the
   * ``default_profile`` field exists on the wire — REQ-AUTH-070). The
   * working state + active-Profile pointer are unchanged — this only updates
   * which Profile is loaded at the next login. Optimistic; rolls back on
   * failure.
   */
  setDefaultProfile: (ref: DefaultProfileRef | null) => Promise<SetPreferencesResult>;
}

export const PreferencesContext = createContext<PreferencesContextValue | null>(null);

/**
 * Read the current preferences + mutators. Throws if called outside
 * a :class:`PreferencesProvider` — that error indicates a missing
 * provider in the tree, not a runtime fault.
 */
export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (ctx === null) {
    throw new Error(
      "usePreferences() called outside a <PreferencesProvider>. Ensure the component tree is wrapped in main.tsx.",
    );
  }
  return ctx;
}
